import { Person, Phone, PhoneDisabled } from "@mui/icons-material";
import { Avatar, Box, Grid, IconButton, Typography } from "@mui/material";
import { useMemo } from "react";
import { useFloatingBubbles } from "../../../../context/floating-bubbles/FloatingBubblesContext";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { formatUSPhone } from "../../../../utils";
import {
  useInternalWebRTC,
  useOptionalInternalWebRTC,
} from "../../../internal-contacts/_context/InternalWebRTCContext";
import { useOptionalInternalTwilioCall } from "../../../internal-twilio-call/context/InternalTwilioCallContext";
import { useVirtualAssistant } from "../../context/VirtualAssistantContext";


import { useVrBulkResolver } from "@components/virtual-receptionist/contacts/hooks/useBulkContactResolver";

import { useBulkResolveContacts } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import {
  getDisplayName,
  getRoleLabel,
} from "@components/virtual-receptionist/contacts/types/bulk-resolver.types";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";

interface Props {
  deviceKey?: string;
}

export const IncomingCall = ({ deviceKey }: Props) => {
  const internalWebRTC = useOptionalInternalWebRTC();
  const internalTwilio = useOptionalInternalTwilioCall();

  if (internalTwilio?.internalCallView === "incoming") {
    return <InternalTwilioIncomingCallUI />;
  }

  if (internalWebRTC?.internalCallView === "incoming") {
    return <InternalIncomingCallUI />;
  }

  return <TwilioIncomingCall deviceKey={deviceKey} />;
};

const InternalTwilioIncomingCallUI = () => {
  const { theme } = useContactCenterTheme();
  const { peerDisplayName, acceptCall, rejectCall } = useOptionalInternalTwilioCall()!;
  const { openCalls } = useFloatingBubbles();

  const handleAccept = () => {
    acceptCall();
    openCalls();
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ height: 560, width: "100%" }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 320,
          height: "100%",
          py: 3,
          marginTop: { xs: -6 },
          marginBottom: { xs: 10 },
        }}
      >
        <Box textAlign="center">
          <Avatar
            sx={{
              width: 96,
              height: 96,
              backgroundColor: theme.shell.accent,
              mx: "auto",
              mb: 3,
              marginTop: { xs: 5 },
            }}
          >
            <Person sx={{ fontSize: 56 }} />
          </Avatar>

          <Typography
            variant="overline"
            sx={{ color: "text.secondary", letterSpacing: 1.2, fontSize: "0.9rem" }}
          >
            Incoming internal call
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 500, mt: 1.5 }}>
            {peerDisplayName}
          </Typography>
        </Box>

        <Grid container spacing={0} alignItems="center">
          <Grid item xs={6} display="flex" justifyContent="center">
            <IconButton
              onClick={() => rejectCall()}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#E53935",
                color: "white",
                "&:hover": { bgcolor: "#D32F2F" },
              }}
            >
              <PhoneDisabled sx={{ fontSize: 32 }} />
            </IconButton>
          </Grid>

          <Grid item xs={6} display="flex" justifyContent="center">
            <IconButton
              onClick={handleAccept}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#4CAF50",
                color: "white",
                "&:hover": { bgcolor: "#45A049" },
              }}
            >
              <Phone sx={{ fontSize: 32 }} />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const InternalIncomingCallUI = () => {
  const { theme } = useContactCenterTheme();
  const {
    peerDisplayName,
    peerSubtitle,
    acceptIncoming,
    rejectIncoming,
  } = useInternalWebRTC();
  const { openCalls } = useFloatingBubbles();

  const handleAccept = () => {
    void acceptIncoming();
    openCalls();
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ height: 560, width: "100%" }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 320,
          height: "100%",
          py: 3,
          marginTop: { xs: -6 },
          marginBottom: { xs: 10 },
        }}
      >
        <Box textAlign="center">
          <Avatar
            sx={{
              width: 96,
              height: 96,
              backgroundColor: theme.shell.accent,
              mx: "auto",
              mb: 3,
              marginTop: { xs: 5 },
            }}
          >
            <Person sx={{ fontSize: 56 }} />
          </Avatar>

          <Typography
            variant="overline"
            sx={{ color: "text.secondary", letterSpacing: 1.2, fontSize: "0.9rem" }}
          >
            Incoming internal call
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 500, mt: 1.5 }}>
            {peerDisplayName}
          </Typography>

          {peerSubtitle && (
            <Typography
              variant="body1"
              sx={{ mt: 1, color: theme.shell.accent, fontWeight: 600 }}
            >
              {peerSubtitle}
            </Typography>
          )}

        </Box>

        <Grid container spacing={0} alignItems="center">
          <Grid item xs={6} display="flex" justifyContent="center">
            <IconButton
              onClick={() => void rejectIncoming()}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#E53935",
                color: "white",
                "&:hover": { bgcolor: "#D32F2F" },
              }}
            >
              <PhoneDisabled sx={{ fontSize: 32 }} />
            </IconButton>
          </Grid>

          <Grid item xs={6} display="flex" justifyContent="center">
            <IconButton
              onClick={() => void handleAccept()}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#4CAF50",
                color: "white",
                "&:hover": { bgcolor: "#45A049" },
              }}
            >
              <Phone sx={{ fontSize: 32 }} />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const getFullPhone = (
  dialCode?: string | null,
  phoneNumber?: string | null,
): string | null => {
  if (!phoneNumber) return null;

  const cleanPhone = phoneNumber.trim();
  const cleanDialCode = dialCode?.trim();

  if (cleanPhone.startsWith("+")) return cleanPhone;

  return cleanDialCode ? `${cleanDialCode}${cleanPhone}` : cleanPhone;
};

const getResolvedDisplayName = (resolved: any): string | null => {
  if (!resolved) return null;

  return (
    getDisplayName(resolved) ||
    resolved.displayName ||
    resolved.fullName ||
    null
  );
};

const getResolvedLabel = (resolved: any): string | null => {
  if (!resolved) return null;

  const label = getRoleLabel(resolved);

  if (label) return label;

  if (resolved.callerType === "client") return "Client";
  if (resolved.callerType === "workforce") return "Workforce";

  return null;
};

const TwilioIncomingCall = ({ deviceKey }: Props) => {
  const { theme } = useContactCenterTheme();
  const {
    incomingCalls,
    currentCompanyState,
    acceptCall,
    hangupCall,
    acceptCallByCompany,
    hangupCallByCompany,
  } = useVirtualAssistant();

  const currentCompany = useAppSelector((state) => state.userState.company);
  const companies = useAppSelector((state) => state.userState.companies) || [];

  const selectedCall = useMemo(() => {
    if (deviceKey) {
      return incomingCalls.find((item) => item.key === deviceKey) || null;
    }

    return incomingCalls[0] || null;
  }, [deviceKey, incomingCalls]);

  const remotePhone =
    selectedCall?.remotePhone ?? currentCompanyState?.remotePhone;

  const resolvedCompanyId =
    selectedCall?.companyId ?? currentCompanyState?.companyId;

  const dialCode = remotePhone?.dialCode;
  const phoneNumber = remotePhone?.phone;
  const fullPhone = getFullPhone(dialCode, phoneNumber);

  const {
    data: legacyResolvedMap = {},
    isLoading: isLoadingLegacyResolved,
  } = useBulkResolveContacts(fullPhone ? [fullPhone] : []);



  const legacyResolved = fullPhone
    ? legacyResolvedMap?.[fullPhone] ?? null
    : null;

  const shouldResolveFromContacts =
    !!fullPhone &&
    !!resolvedCompanyId &&
    !legacyResolved &&
    !isLoadingLegacyResolved;

  const { data: contactsResolvedMap = {} } = useVrBulkResolver(
    resolvedCompanyId,
    shouldResolveFromContacts ? [fullPhone] : [],
    shouldResolveFromContacts,
  );

  const contactResolved = fullPhone
    ? contactsResolvedMap?.[fullPhone] ?? null
    : null;

  const resolvedContact = legacyResolved || contactResolved || null;

  const displayName = selectedCall?.isInternalIncoming
    ? selectedCall.internalCallerName
    : getResolvedDisplayName(resolvedContact);
  const callerLabel = selectedCall?.isInternalIncoming ? "Internal" : getResolvedLabel(resolvedContact);

  const receivingCompanyName = useMemo(() => {
    if (selectedCall?.isInternalIncoming && selectedCall.internalCallerCompanyName) {
      return selectedCall.internalCallerCompanyName;
    }

    if (!resolvedCompanyId) return undefined;

    if (String(currentCompany?.id) === String(resolvedCompanyId)) {
      return currentCompany?.businessName;
    }

    const matchedCompany = companies.find(
      (item: any) =>
        String(item.companyId) === String(resolvedCompanyId) ||
        String(item.company?.id) === String(resolvedCompanyId),
    );

    return matchedCompany?.company?.businessName;
  }, [resolvedCompanyId, currentCompany, companies, selectedCall?.isInternalIncoming, selectedCall?.internalCallerCompanyName]);

  const onReject = () => {
    if (selectedCall?.key) {
      hangupCallByCompany(selectedCall.key);
      return;
    }

    hangupCall();
  };

  const onAccept = () => {
    if (selectedCall?.key) {
      acceptCallByCompany(selectedCall.key);
      return;
    }

    acceptCall();
  };

  const formattedNumber = (() => {
    if (!phoneNumber) return "Unknown number";

    if (dialCode === "+1") {
      return `${dialCode} ${formatUSPhone(phoneNumber)}`;
    }

    return dialCode ? `${dialCode} ${phoneNumber}` : phoneNumber;
  })();

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        height: 560,
        width: "100%",
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 320,
          height: "100%",
          py: 3,
          marginTop: { xs: -6 },
          marginBottom: { xs: 10 },
        }}
      >
        <Box textAlign="center">
          <Avatar
            sx={{
              width: 96,
              height: 96,
              backgroundColor: theme.shell.accent,
              mx: "auto",
              mb: 3,
              marginTop: { xs: 5 },
            }}
          >
            <Person sx={{ fontSize: 56 }} />
          </Avatar>

          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              letterSpacing: 1.2,
              fontSize: "0.9rem",
            }}
          >
            Incoming {selectedCall?.isInternalIncoming ? "internal call" : "call"}
          </Typography>

          {receivingCompanyName && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "text.secondary",
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              Calling {receivingCompanyName}
            </Typography>
          )}

          {displayName && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 500,
                mt: 1.5,
              }}
            >
              {displayName}
              {callerLabel && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    fontSize: "0.95rem",
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  ({callerLabel})
                </Box>
              )}
            </Typography>
          )}

          {!selectedCall?.isInternalIncoming && (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 400,
                letterSpacing: 1.5,
                mt: displayName ? 1 : 1.5,
              }}
            >
              {formattedNumber}
            </Typography>
          )}
        </Box>

        <Grid container spacing={0} alignItems="center">
          <Grid item xs={6} display="flex" justifyContent="center">
            <IconButton
              onClick={onReject}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#E53935",
                color: "white",
                "&:hover": {
                  bgcolor: "#D32F2F",
                },
              }}
            >
              <PhoneDisabled sx={{ fontSize: 32 }} />
            </IconButton>
          </Grid>

          <Grid item xs={6} display="flex" justifyContent="center">
            <IconButton
              onClick={onAccept}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#4CAF50",
                color: "white",
                "&:hover": {
                  bgcolor: "#45A049",
                },
              }}
            >
              <Phone sx={{ fontSize: 32 }} />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};